import { forwardRef, useImperativeHandle, useRef } from 'react';
import dayjs from 'dayjs';
import SignatureCanvas from 'react-signature-canvas';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { dataURLToFile } from '@shared/lib';

import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  CompanyName,
  SignatureBand,
  SignedAt,
  Title,
  TopContent,
  TopLeft,
  TopRight,
  UploadedSignature,
  Wrapper,
} from './Signature.styles';

// react-signature-canvas types `canvasProps` as CanvasHTMLAttributes, which rejects
// data-* in a fresh object literal. Passing a NAMED object skips the excess-property
// check while still forwarding the test id to the underlying <canvas>.
//
// `width`/`height` here are the canvas ATTRIBUTES (bitmap resolution). The matching CSS box is set
// on `TopRight`'s `& canvas` rule from the same two constants — the CSS class the SCSS used to add
// is gone, because `canvasProps` is the only channel this vendor component exposes.
const CANVAS_PROPS = {
  width: CANVAS_WIDTH,
  height: CANVAS_HEIGHT,
  'data-testid': 'signature-canvas',
};

export type SignatureProps = {
  title?: string;
  companyName?: string;
  date?: Date;
  image?: string;
  onSignatureChange?: () => void;
};

/**
 * The imperative handle, exported so callers stop re-declaring it.
 *
 * The `forwardRef` generic used to name only `getSignatureFile` while `useImperativeHandle` also
 * returned `clearCanvas` — so the declared ref type was narrower than the real one, and the single
 * consumer (`SignaturePreviewPopup`) hand-rolled its own matching type to call `clearCanvas()`.
 * Two declarations of one contract, neither checked against the implementation. Corrected during
 * the move to `shared/ui`, per the FRONTEND_MIGRATION.md precedent that a suppression-worthy type
 * usually marks a real defect.
 */
export type SignatureHandle = {
  getSignatureFile: () => File;
  clearCanvas: () => void;
};

const Signature = forwardRef<SignatureHandle, SignatureProps>(
  ({ title = '', companyName = '', date, image, onSignatureChange }, ref) => {
    const { t } = useTranslation('dashboard', { keyPrefix: 'global' });
    const theme = useTheme();
    const sigCanvasRef = useRef<SignatureCanvas>(null);

    useImperativeHandle(ref, () => ({
      getSignatureFile: () => {
        if (sigCanvasRef.current) {
          const dataURL = sigCanvasRef.current.getCanvas().toDataURL('image/png');
          return dataURLToFile(dataURL, 'signature.png');
        }
        return new File([], 'empty_signature.png', { type: 'image/png' });
      },
      clearCanvas: () => {
        if (sigCanvasRef.current) {
          sigCanvasRef.current.clear();
        }
      },
    }));

    return (
      <Wrapper>
        <TopContent>
          <TopLeft>
            <div>
              <Title>{title}</Title>
              <CompanyName>{companyName}</CompanyName>
            </div>
            <SignedAt>{dayjs(date).format('DD/MM/YY HH:mm')}</SignedAt>
          </TopLeft>
          <TopRight>
            {image && <UploadedSignature src={image} alt='signature' />}
            <SignatureCanvas
              ref={sigCanvasRef}
              // `penColor` is a plain string prop on a vendor component, so it cannot be styled —
              // it has to be read off the theme here. Previously the literal '#003e47', the same
              // stale pre-Figma brand dark the SCSS carried.
              penColor={theme.palette.primary.darker}
              canvasProps={CANVAS_PROPS}
              onBegin={onSignatureChange}
            />
          </TopRight>
        </TopContent>
        <SignatureBand>{t('qualified_electronic_signature')}</SignatureBand>
      </Wrapper>
    );
  },
);

// `forwardRef` returns an anonymous object, so React DevTools and the `react/display-name` lint
// rule both see a nameless component. Set it explicitly now the file lints at `error` severity
// under the `shared/**` ratchet.
Signature.displayName = 'Signature';

export default Signature;
